import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { ApiError } from "./api";

// Data cache: stores { data, timestamp } by cache key
type CacheEntry<T> = { data: T; timestamp: number };
const dataCache = new Map<string, CacheEntry<unknown>>();
type InflightRequest<T> = {
  controller: AbortController;
  promise: Promise<T>;
  subscribers: number;
};
// In-flight request deduplication: concurrent calls with same cache key share one request
const inflightRequests = new Map<string, InflightRequest<unknown>>();

// Default cache TTL: 120 seconds
const DEFAULT_CACHE_TTL = 120 * 1000; // 120 seconds

// Global cache revision counter — incremented on clearApiCache() so all
// useApiState hooks re-run their fetch effects.
let cacheRevision = 0;
const cacheRevisionListeners = new Set<() => void>();

function subscribeCacheRevision(listener: () => void) {
  cacheRevisionListeners.add(listener);
  return () => {
    cacheRevisionListeners.delete(listener);
  };
}

function getCacheRevision() {
  return cacheRevision;
}

function acquireInflightRequest<T>(
  cacheKey: string,
  fn: (signal: AbortSignal) => Promise<T>
) {
  let request = inflightRequests.get(cacheKey) as InflightRequest<T> | undefined;

  if (!request) {
    const controller = new AbortController();
    const createdRequest: InflightRequest<T> = {
      controller,
      promise: Promise.resolve()
        .then(() => fn(controller.signal))
        .finally(() => {
          if (inflightRequests.get(cacheKey) === createdRequest) {
            inflightRequests.delete(cacheKey);
          }
        }),
      subscribers: 0,
    };
    request = createdRequest;
    inflightRequests.set(cacheKey, request);
  }

  request.subscribers += 1;
  let released = false;

  return {
    promise: request.promise,
    release() {
      if (released) return;
      released = true;
      request.subscribers -= 1;

      if (
        request.subscribers === 0 &&
        inflightRequests.get(cacheKey) === request
      ) {
        inflightRequests.delete(cacheKey);
        request.controller.abort();
      }
    },
  };
}

// Configurable options for useApiState
type UseApiStateOptions = {
  // Time in ms before cached data is considered stale (default: 30s)
  cacheTtl?: number;
  // Cache key for deduplication. If not provided, uses hookId + stringified deps
  cacheKey?: string;
  // Whether to skip initial fetch (useful for conditional fetching)
  skip?: boolean;
  // Keep previous data while revalidating (stale-while-revalidate)
  keepPreviousData?: boolean;
  // Refetch stale data when browser window regains focus (default: false)
  refetchOnFocus?: boolean;
};

export function useApiState<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  options: UseApiStateOptions = {}
) {
  const {
    cacheTtl = DEFAULT_CACHE_TTL,
    cacheKey: customCacheKey,
    skip = false,
    keepPreviousData = true,
    refetchOnFocus = false,
  } = options;

  // Subscribe to cache revision so we re-fetch when clearApiCache() is called
  const revision = useSyncExternalStore(subscribeCacheRevision, getCacheRevision);

  // Use React's useId for a stable unique identifier per hook instance
  // This prevents cache key collisions between different components with same deps
  const hookId = useId();

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(!skip);
  const [errors, setErrors] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  // Generate cache key from hookId + deps or use custom key
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const depsKey = useMemo(() => JSON.stringify(deps), deps);
  const cacheKey = customCacheKey ?? `${hookId}:${depsKey}`;

  // Store the current AbortController for the reload function
  const abortControllerRef = useRef<AbortController | null>(null);

  // Execute the fetch - this is called from the effect
  const execute = useCallback(
    async (signal: AbortSignal, forceRefresh = false) => {
      if (skip) return;

      // Check cache first
      const cached = dataCache.get(cacheKey) as CacheEntry<T> | undefined;
      const isCacheValid = cached && Date.now() - cached.timestamp < cacheTtl;

      // If we have valid cached data and not forcing refresh, use it
      if (isCacheValid && !forceRefresh) {
        setData(cached.data);
        setLoading(false);
        setErrors([]);
        return;
      }

      // If we have stale cached data and keepPreviousData is true, show it while revalidating
      if (cached && keepPreviousData && !forceRefresh) {
        setData(cached.data);
        setIsValidating(true);
        // Don't show loading spinner when we have stale data
        setLoading(false);
      } else {
        setLoading(true);
      }

      setErrors([]);

      try {
        if (signal.aborted) {
          return;
        }

        // Deduplicate concurrent in-flight requests for the same cache key
        const subscription = acquireInflightRequest(cacheKey, fn);
        const releaseSubscription = () => subscription.release();
        signal.addEventListener("abort", releaseSubscription, { once: true });

        let result: T;
        try {
          result = await subscription.promise;
        } finally {
          signal.removeEventListener("abort", releaseSubscription);
          subscription.release();
        }

        // Only update state if request wasn't aborted
        if (!signal.aborted) {
          setData(result);
          setErrors([]);
          // Update cache
          dataCache.set(cacheKey, { data: result, timestamp: Date.now() });
        }
      } catch (err) {
        // Silently ignore aborted requests
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }
        if (signal.aborted) {
          return;
        }

        if (err instanceof ApiError) {
          setErrors(err.errors);
        } else {
          setErrors(["Something went wrong"]);
        }
        // Keep showing stale data on error if available
        if (!keepPreviousData) {
          setData(null);
        }
      } finally {
        // Only update loading state if request wasn't aborted
        if (!signal.aborted) {
          setLoading(false);
          setIsValidating(false);
        }
      }
    },
    [cacheKey, cacheTtl, skip, keepPreviousData, fn]
  );

  // Main effect that handles fetching and cleanup
  useEffect(() => {
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    void execute(abortController.signal);

    // Cleanup: abort the request when deps change or component unmounts
    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, skip, revision, ...deps]);

  // Effect for refetching stale data when window regains focus
  useEffect(() => {
    if (!refetchOnFocus) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const cached = dataCache.get(cacheKey) as CacheEntry<T> | undefined;
        const isStale = !cached || Date.now() - cached.timestamp >= cacheTtl;
        if (isStale) {
          // Abort any in-flight request before starting a new one
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          const abortController = new AbortController();
          abortControllerRef.current = abortController;
          void execute(abortController.signal, true);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refetchOnFocus, cacheKey, cacheTtl, execute]);

  // Reload function that forces a refresh
  const reload = useCallback(() => {
    // Abort any in-flight request before starting a new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    return execute(abortController.signal, true);
  }, [execute]);

  // Mutate function to update cached data without refetching
  const mutate = useCallback(
    (newData: T | ((prev: T | null) => T)) => {
      const resolvedData = typeof newData === "function"
        ? (newData as (prev: T | null) => T)(data)
        : newData;

      setData(resolvedData);
      dataCache.set(cacheKey, { data: resolvedData, timestamp: Date.now() });
    },
    [cacheKey, data]
  );

  return {
    data,
    loading,
    errors,
    isValidating,
    reload,
    mutate,
  };
}

// Utility to clear the entire cache (useful for logout and center switching)
export function clearApiCache(): void {
  dataCache.clear();
  cacheRevision++;
  cacheRevisionListeners.forEach((l) => l());
}

// Utility to invalidate specific cache entries by key pattern
export function invalidateCache(pattern: string | RegExp): void {
  const regex = typeof pattern === "string" ? new RegExp(pattern) : pattern;

  for (const key of dataCache.keys()) {
    if (regex.test(key)) {
      dataCache.delete(key);
    }
  }
}

// Utility to prefetch data into cache
export async function prefetch<T>(
  fn: () => Promise<T>,
  cacheKey: string
): Promise<T> {
  const result = await fn();
  dataCache.set(cacheKey, { data: result, timestamp: Date.now() });
  return result;
}
